import {
  checkPuzzelSession,
  redirectToPuzzelLogin,
} from "app/PuzzelAuth/puzzelAuth";
import {
  clearPuzzelTokens,
  subscribeToPuzzelLogoutBroadcast,
} from "app/PuzzelAuth/puzzelTokenStorage";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

type PuzzelAuthenticationValue = {
  isAuthenticated: boolean;
  login: () => void;
  phoneNumberPrefix: string;
  functionalNumberPrefix: string;
  countryCode: string;
  maxAttempts: number;
  secondsBetweenAttempts: number;
  ciqType: string;
  queueKey: string;
  maxOneRequestForPreferredAgent: boolean;
  preferredAgentWaitTimeSeconds: number;
  reservedAgentWaitTimeSeconds: number;
};

const noop = () => {};

const initialValue: PuzzelAuthenticationValue = {
  isAuthenticated: false,
  login: noop,
  ciqType: "",
  phoneNumberPrefix: "",
  functionalNumberPrefix: "",
  countryCode: "",
  maxAttempts: 0,
  maxOneRequestForPreferredAgent: false,
  preferredAgentWaitTimeSeconds: 0,
  queueKey: "",
  reservedAgentWaitTimeSeconds: 0,
  secondsBetweenAttempts: 0,
};

// TODO: call-routing config used to come from the backend login response - needs a new home.
const puzzelOrgConfig: Omit<
  PuzzelAuthenticationValue,
  "isAuthenticated" | "login"
> = {
  ciqType: "",
  phoneNumberPrefix: "",
  functionalNumberPrefix: "",
  countryCode: "",
  maxAttempts: 0,
  maxOneRequestForPreferredAgent: false,
  preferredAgentWaitTimeSeconds: 0,
  queueKey: "",
  reservedAgentWaitTimeSeconds: 0,
  secondsBetweenAttempts: 0,
};

const PuzzelAuthenticationContext = React.createContext(initialValue);

// Re-validates in the background so a dead session doesn't keep showing as "logged in".
const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const PuzzelAuthenticationProvider = (
  props: Record<string, unknown>,
): ReactElement => {
  const [puzzelContext, setPuzzelContext] =
    useState<PuzzelAuthenticationValue>(initialValue);

  // Non-redirecting check - the dashboard should work without Puzzel login, only the phone widget needs it.
  const checkAuthentication = () => {
    checkPuzzelSession()
      .then((isAuthenticated) => {
        setPuzzelContext(
          isAuthenticated
            ? {
                isAuthenticated: true,
                login: noop,
                ...puzzelOrgConfig,
              }
            : initialValue,
        );
      })
      .catch((error) => {
        log(
          LogLevel.error,
          "PuzzelAuthenticationProvider",
          "Failed to check Puzzel session",
          error,
        );
      });
  };

  useEffect(() => {
    checkAuthentication();
    const intervalId = window.setInterval(
      checkAuthentication,
      SESSION_CHECK_INTERVAL_MS,
    );

    // Cross-tab logout signal - sent on a full Vy logout, so drop this tab's Puzzel session too.
    const unsubscribeLogoutBroadcast = subscribeToPuzzelLogoutBroadcast(() => {
      clearPuzzelTokens();
      setPuzzelContext(initialValue);
    });

    return () => {
      window.clearInterval(intervalId);
      unsubscribeLogoutBroadcast();
    };
  }, []);

  const login = useCallback(() => {
    void redirectToPuzzelLogin();
  }, []);

  const value = useMemo(
    () => ({ ...puzzelContext, login }),
    [puzzelContext, login],
  );

  return <PuzzelAuthenticationContext.Provider value={value} {...props} />;
};

function usePuzzelAuthenticationContext(): PuzzelAuthenticationValue {
  const context = React.useContext(PuzzelAuthenticationContext);
  if (context === undefined) {
    throw new Error(
      "usePuzzelContext must be used within a PuzzelContextProvider",
    );
  }
  return context;
}

export { PuzzelAuthenticationProvider, usePuzzelAuthenticationContext };
